<template>
    <!-- Компактный блок в карточке и полная вкладка — один компонент, но разная плотность:
         в карточке лента не должна перевешивать сам предмет. -->
    <div class="inventory-history" :class="{ 'inventory-history_compact': compact }">
        <div class="inventory-history__loader" v-if="isLoading && !events.length">
            <VSpinner></VSpinner>
        </div>

        <template v-else>
            <p class="inventory-history__error" v-if="error">
                {{ error }}
            </p>

            <!-- Журнал научился привязывать события к предмету не с самого начала:
                 всё, что происходило раньше, в ленту не попадает и не может попасть. -->
            <p class="inventory-history__notice" v-if="showPartialNotice">
                Журнал ведётся не с начала. События до {{ loggingSinceText }} не сохранились.
            </p>

            <p class="inventory-history__empty" v-if="!visibleEvents.length && !error">Событий пока нет.</p>

            <ul class="inventory-history__list" v-else>
                <li
                    class="inventory-history__point inventory-history-point"
                    :key="event.id"
                    v-for="event in visibleEvents"
                >
                    <!-- Дата со временем, а не одна дата: события одного дня иначе
                         не различить, а порядок в ленте становится необъяснимым.
                         В компактном блоке дата встаёт над названием — см. стили. -->
                    <div class="inventory-history-point__header">
                        <h4 class="inventory-history-point__title">{{ title(event) }}</h4>

                        <span class="inventory-history-point__occurred-at" :title="event.occurred_at">
                            {{ $_toLocalDateShort(event.occurred_at) }}
                        </span>
                    </div>

                    <!-- Автор и описание одной строкой: в карточке блок событий не должен
                         перевешивать сам предмет. В компактном виде строка ужимается
                         до двух, на полной вкладке переносится целиком.

                         Описание собирается на фронте по type и payload; текст с бэкенда
                         (английский, машинный) остаётся запасным для незнакомых кодов. -->
                    <p class="inventory-history-point__meta">
                        <span class="inventory-history-point__actor">
                            {{ event.actor ? event.actor.display_name : 'Автор неизвестен' }}
                        </span>

                        <span class="inventory-history-point__description" v-if="description(event)">
                            {{ description(event) }}
                        </span>
                    </p>
                </li>
            </ul>

            <!-- В компактном режиме подгрузки нет: блок «Последние события» показывает
                 ровно последние несколько, а за остальным отправляет на полную вкладку.
                 Карточка ставит такую ссылку в заголовок блока и отключает эту — см.
                 показ-все-ссылку show-all-link. -->
            <div class="inventory-history__more" v-if="compact && showAllLink && hasHiddenEvents">
                <button type="button" class="inventory-history__link" @click="$emit('show-all')">Вся история</button>
            </div>

            <div class="inventory-history__more" v-else-if="hasMore">
                <VButton type="button" class="button_green-light" :disabled="isLoading" @click="loadMore">
                    {{ isLoading ? 'Загрузка…' : 'Показать ещё' }}
                </VButton>
            </div>
        </template>
    </div>
</template>
<script>
// Components
import VButton from '../../../components/VButton/VButton.vue';
import VSpinner from '../../../components/VProgress/VSpinner.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { getInventoryItemHistory } from '../../../../js/inventory-api';
import { errorMessage } from '../../../../js/error';
import InventoryLogType from '../../../../js/enums/InventoryLogType';
import InventoryLogTypeTitle from '../../../../js/enums/InventoryLogTypeTitle';
import describeInventoryEvent from '../../../../js/inventory-event-description';

/**
 * Мелкие события: правки отдельных атрибутов. В компактном блоке они скрыты — одна правка
 * карточки пишет смену ответственного плюс по событию на каждый тронутый атрибут,
 * и крупные события вытесняются мелкими.
 */
const MINOR_EVENT_TYPES = [
    InventoryLogType.ITEM_ATTRIBUTE_CREATED,
    InventoryLogType.ITEM_ATTRIBUTE_UPDATED,
    InventoryLogType.ITEM_ATTRIBUTE_DELETED,
];

/**
 * События атрибутов. Для них англоязычный action с бэкенда пользователю не показываем:
 * это внутренний audit-текст вида «An inventory item attribute (id: 68) has been updated»
 * с идентификатором строки. Если русское описание не собралось — событие скрывается.
 */
const ATTRIBUTE_EVENT_TYPES = MINOR_EVENT_TYPES;

/**
 * Во сколько раз компактный блок запрашивает больше событий, чем показывает.
 *
 * Мелкие события отсеиваются уже на клиенте, и без запаса страница из трёх записей,
 * где две — правки атрибутов, показала бы одну строку.
 */
const COMPACT_FETCH_FACTOR = 5;

@Component({
    components: {
        VButton,
        VSpinner,
    },
})
class VInventoryItemHistory extends Vue {
    @Prop({ type: Number, required: true })
    itemId;

    // Компактный режим — блок «Последние события» в карточке: несколько записей
    // и ссылка на полную вкладку вместо кнопки подгрузки.
    @Prop({ type: Boolean, default: false })
    compact;

    @Prop({ type: Number, default: 20 })
    perPage;

    // Своя ссылка «Вся история» внизу компактной ленты. Карточка оборудования ставит такую
    // в заголовок блока и эту отключает: две одинаковые ссылки в одном блоке — шум.
    @Prop({ type: Boolean, default: true })
    showAllLink;

    events = [];
    meta = null;
    page = 1;
    isLoading = false;
    error = '';

    get hasMore() {
        return this.meta ? this.meta.current_page < this.meta.last_page : false;
    }

    get showPartialNotice() {
        // В компактном блоке предупреждение о неполноте журнала не показываем: на трёх
        // событиях оно занимает больше места, чем сами события. На полной вкладке остаётся.
        if (this.compact) {
            return false;
        }

        return Boolean(this.meta && this.meta.history_is_partial && this.meta.logging_since);
    }

    /**
     * События для показа.
     *
     * В компактном блоке правки отдельных атрибутов скрыты: одна правка карточки пишет
     * «Ответственный изменён» плюс два «Атрибут изменён», и крупные события вытесняются
     * мелкими. На полной вкладке видно всё.
     */
    get visibleEvents() {
        if (!this.compact) {
            return this.events.filter((event) => this.isPresentable(event));
        }

        return this.majorEvents.slice(0, this.perPage);
    }

    /**
     * Крупные события компактного блока — до обрезки до perPage.
     */
    get majorEvents() {
        return this.events.filter((event) => this.isPresentable(event) && !MINOR_EVENT_TYPES.includes(event.type));
    }

    /**
     * Есть ли что смотреть на полной вкладке.
     *
     * Одного hasMore мало: страница истории бывает одна, а блок всё равно показывает только
     * последние три события и прячет остальные — вместе с правками атрибутов, которых
     * в компактном виде нет вовсе. Без этой проверки ссылка «Вся история» пропадала ровно
     * тогда, когда была нужнее всего.
     */
    get hasHiddenEvents() {
        return this.hasMore || this.majorEvents.length > this.perPage || this.events.length > this.majorEvents.length;
    }

    /**
     * Можно ли показать событие пользователю.
     *
     * Событие атрибута без собранного русского описания скрывается: показывать вместо него
     * английский audit-текст с идентификатором строки — хуже, чем не показывать ничего.
     *
     * Так отсеиваются накопленные «Атрибут изменён»: журнал писал по ним только новое
     * значение, без названия атрибута и без старого, и описать их нечем. Новые события
     * несут название и обе стороны изменения, поэтому описание для них собирается
     * и они показываются. Старую историю не мигрируем — она так и останется скрытой.
     */
    isPresentable(event) {
        if (!ATTRIBUTE_EVENT_TYPES.includes(event.type)) {
            return true;
        }

        return Boolean(describeInventoryEvent(event));
    }

    // Компактный блок берёт с запасом и режет на клиенте: сколько из страницы окажется
    // мелкими правками атрибутов, заранее неизвестно.
    get requestPerPage() {
        return this.compact ? this.perPage * COMPACT_FETCH_FACTOR : this.perPage;
    }

    get loggingSinceText() {
        return this.meta && this.meta.logging_since ? this.$_toLocalDateHistory(this.meta.logging_since) : '';
    }

    title(event) {
        // Незнакомый код не прячем: показываем описание с бэкенда, в крайнем случае сам код —
        // иначе новые типы событий выглядели бы в старом фронте как пустые строки.
        return InventoryLogTypeTitle[event.type] || event.description || event.type;
    }

    description(event) {
        const text = describeInventoryEvent(event);

        // Английский action с бэкенда — фолбэк для незнакомых кодов: они появятся в журнале
        // раньше, чем этот фронт будет выкачен. Для событий атрибутов фолбэк отключён:
        // там это внутренний audit-текст с идентификатором строки, а не описание.
        if (ATTRIBUTE_EVENT_TYPES.includes(event.type)) {
            return text || '';
        }

        return text || event.description || '';
    }

    async loadMore() {
        await this.fetch(this.page + 1);
    }

    async fetch(page) {
        this.isLoading = true;
        this.error = '';

        try {
            const response = await getInventoryItemHistory(this.itemId, page, this.requestPerPage);

            // Дописываем, а не заменяем: «Показать ещё» продолжает ленту.
            this.events = page === 1 ? response.data.data : [...this.events, ...response.data.data];
            this.meta = response.data.meta;
            this.page = page;
        } catch (error) {
            // Ошибку показываем внутри вкладки, а не модалкой поверх модалки.
            this.error = errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    mounted() {
        this.fetch(1);
    }
}

export default VInventoryItemHistory;
</script>
<style lang="scss">
:root {
    --inventory-history-title-color: #202124;
    --inventory-history-actor-color: #5f6368;
    --inventory-history-muted-color: #9aa0a6;
    --inventory-history-point-border: #e4eaef;
    --inventory-history-compact-line: #e8eaed;
    --inventory-history-notice-background: #fdf0e7;
    --inventory-history-notice-color: #d9641e;
}

.inventory-history {
    &__loader {
        display: flex;
        justify-content: center;
        padding: 24px 0;
    }

    // Лента, а не стопка карточек: события живут внутри карточки оборудования, и рамка
    // у каждого давала карточки внутри карточки. Точка и вертикальная нить читаются
    // как хронология и занимают меньше места.
    &__list {
        position: relative;
        margin: 0;
        padding: 0 0 0 18px;
        list-style: none;
    }

    &__notice {
        margin: 0 0 16px 0;
        padding: 12px 16px;
        border-radius: 8px;
        background: var(--inventory-history-notice-background);
        color: var(--inventory-history-notice-color);
        font-size: 0.8125rem;
        line-height: 1.125rem;
    }

    &__error {
        margin: 0 0 16px 0;
        color: var(--color-red);
        font-size: 0.8125rem;
    }

    &__empty {
        margin: 24px 0;
        color: var(--inventory-history-muted-color);
        font-size: 0.875rem;
        text-align: center;
    }

    &__more {
        display: flex;
        justify-content: center;
        margin: 16px 0 0 0;
    }

    &__link {
        padding: 0;
        border: 0;
        background: none;
        color: var(--color-green);
        font-size: 0.8125rem;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    // Компактная лента в карточке оборудования. Она стоит в узкой правой колонке, поэтому
    // строка «название — дата» в ней не помещается: дата встаёт над названием отдельной
    // тихой строкой, а автор с описанием — под ним, с обрезкой по двум строкам.
    &_compact {
        .inventory-history__list {
            padding-left: 0;
        }

        .inventory-history__empty {
            margin: 6px 0 0 0;
            font-size: 0.75rem;
            text-align: left;
        }

        .inventory-history__notice,
        .inventory-history__error {
            margin-bottom: 8px;
            font-size: 0.75rem;
        }

        .inventory-history__loader {
            padding: 12px 0;
        }

        .inventory-history__more {
            justify-content: flex-start;
            margin-top: 10px;
        }

        .inventory-history__link {
            font-size: 0.75rem;
        }

        .inventory-history-point {
            margin-bottom: 0;
            padding: 8px 0;
            border-top: 1px solid var(--inventory-history-compact-line);

            // Ни точек, ни нити: в блоке на три события хронологию держит сам порядок,
            // а лишняя графика делала бы его самым заметным элементом карточки.
            &::before,
            &::after {
                display: none;
            }

            &:first-child {
                padding-top: 4px;
                border-top: 0;
            }

            &:last-child {
                padding-bottom: 0;
            }
        }

        // Дата первой строкой — за ней название события.
        .inventory-history-point__header {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }

        .inventory-history-point__title {
            margin: 1px 0 0 0;
            font-size: 0.8125rem;
            line-height: 1.125rem;
            font-weight: 600;
        }

        .inventory-history-point__occurred-at {
            order: -1;
            font-size: 0.6875rem;
            line-height: 0.9375rem;
        }

        // Автор и описание: не больше двух строк, дальше многоточие. Полный текст —
        // на вкладке «История».
        .inventory-history-point__meta {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            margin-top: 2px;
            font-size: 0.75rem;
            line-height: 1rem;
        }

        .inventory-history-point__actor {
            color: var(--inventory-history-actor-color);
        }
    }
}

.inventory-history-point {
    position: relative;
    margin: 0 0 14px 0;

    // Точка события и нить до следующего. У последнего события нить обрывается —
    // иначе лента выглядит незаконченной.
    &::before {
        content: '';
        position: absolute;
        left: -18px;
        top: 5px;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #c9ced4;
    }

    &::after {
        content: '';
        position: absolute;
        left: -15px;
        top: 16px;
        bottom: -14px;
        width: 1px;
        background: var(--inventory-history-point-border);
    }

    &:last-child {
        margin-bottom: 0;

        &::after {
            display: none;
        }
    }

    &__header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
    }

    // Название события — главное в точке ленты: за ним следят глазами сверху вниз.
    &__title {
        margin: 0 8px 0 0;
        font-size: 0.8125rem;
        line-height: 1.125rem;
        font-weight: 600;
        color: var(--inventory-history-title-color);
    }

    &__occurred-at {
        flex-shrink: 0;
        font-size: 0.6875rem;
        line-height: 1.125rem;
        color: var(--inventory-history-muted-color);
        white-space: nowrap;
    }

    // Строка «автор — описание». На полной вкладке переносится, в карточке ужимается
    // до двух строк — см. .inventory-history_compact.
    &__meta {
        display: block;
        margin: 2px 0 0 0;
        font-size: 0.75rem;
        line-height: 1.0625rem;
        color: var(--inventory-history-muted-color);
        word-break: break-word;
    }

    &__actor {
        color: var(--inventory-history-actor-color);
    }

    &__description {
        color: var(--inventory-history-muted-color);

        &::before {
            content: '·';
            margin: 0 4px;
        }
    }
}
</style>
